// The request lifecycle every reducer here shares. redux-promise-middleware turns an action whose
// payload is a promise into TYPE_LOADING, then TYPE_SUCCESS or TYPE_ERROR; this module is the one
// copy of what those three transitions do to a slice. The action type strings and the state keys
// stay exactly what the reducers wrote out by hand before: selectors across the app read them as
// strings.

// The three flags a request sets; a request may name its own (see rewardReducer) or `loading: null`.
export const REQUEST_FLAGS = { loading: 'isLoading', success: 'success', error: 'error' };

// The server's body out of an *_ERROR payload (an axios response, an axios error, or nothing; see
// src/utils/interceptor.js). With no response and no `data` it is `false`: screens read `error[0]`.
export const errorBody = payload => {
  if (!payload) {
    return false;
  }
  if (payload.response) {
    return payload.response.data;
  }
  return 'data' in Object(payload) ? payload.data : false;
};

// Maps a successful response onto one state key; without a response the key keeps its value.
export const dataAs = key => action => (action.payload ? { [key]: action.payload.data } : {});

// Appends a page to a paginated list; without a response the list stays as it is.
export const appendPage = (listKey, totalKey) => (action, state) =>
  action.payload
    ? {
        [listKey]: [...state[listKey], ...action.payload.data],
        [totalKey]: action.payload.headers['x-pagination-total-count'],
      }
    : {};

const noFields = () => ({});

const flagsFor = (flags, loading, success, error) => {
  const next = { [flags.success]: success, [flags.error]: error };
  if (flags.loading) {
    next[flags.loading] = loading;
  }
  return next;
};

// The three handlers of one request. `success`, `loading` and `failure` return the extra fields
// each transition writes next to the flags; `error` turns the rejection into what the error flag
// holds (the server body by default).
export const requestCases = (
  type,
  { success = noFields, loading = noFields, failure = noFields, flags = REQUEST_FLAGS, error = errorBody } = {}
) => ({
  [`${type}_LOADING`]: (state, action) => ({
    ...state,
    ...loading(action, state),
    ...flagsFor(flags, true, false, false),
  }),
  [`${type}_SUCCESS`]: (state, action) => ({
    ...state,
    ...flagsFor(flags, false, true, false),
    ...success(action, state),
  }),
  [`${type}_ERROR`]: (state, action) => ({
    ...state,
    ...failure(action, state),
    ...flagsFor(flags, false, false, error(action.payload)),
  }),
});

// A paged list's request cases that drop a result its list was reset after (CO-PAGED-STALE-RESPONSE).
// Each request carries meta.requestId (actions/requestMeta.js); _LOADING records it under
// `pendingKey`, the list's reset clears that record, and a _SUCCESS or _ERROR whose id is no longer
// recorded does not touch the list, its total or its error — no late page from an old filter or an
// earlier refresh. Unlike home's single current request, every recorded request stays valid until a
// reset, so two load-more pages in flight both land. An action without an id is applied as before.
// The loading flag follows the recorded requests: it stays set while one is still pending, and a
// dropped result clears it once none is, so a reset that no new request follows cannot leave the
// list loading for good.
export const pagedRequestCases = (type, pendingKey, options = {}) => {
  const cases = requestCases(type, options);
  const loadingFlag = (options.flags || REQUEST_FLAGS).loading;
  const requestIdOf = action => action.meta && action.meta.requestId;
  const settled = handler => (state, action) => {
    const id = requestIdOf(action);
    if (id === undefined || id === null) return handler(state, action);
    const pending = state[pendingKey] || [];
    if (pending.indexOf(id) === -1) {
      return loadingFlag && pending.length === 0 && state[loadingFlag] ? { ...state, [loadingFlag]: false } : state;
    }
    const stillPending = pending.filter(other => other !== id);
    const next = { ...handler(state, action), [pendingKey]: stillPending };
    if (loadingFlag && stillPending.length > 0) next[loadingFlag] = true;
    return next;
  };
  return {
    [`${type}_LOADING`]: (state, action) => {
      const id = requestIdOf(action);
      const next = cases[`${type}_LOADING`](state, action);
      return id === undefined || id === null ? next : { ...next, [pendingKey]: [...(state[pendingKey] || []), id] };
    },
    [`${type}_SUCCESS`]: settled(cases[`${type}_SUCCESS`]),
    [`${type}_ERROR`]: settled(cases[`${type}_ERROR`]),
  };
};

// A reducer from a map of action type to handler. Each slice starts from its own shallow copy of
// the shared default (a write to one slice root must not reach the others), and an action the map
// does not name returns the state it was given, so an unrelated action is not a change.
export const createReducer = (initialState, handlers) => (state = { ...initialState }, action = {}) =>
  Object.prototype.hasOwnProperty.call(handlers, action.type) ? handlers[action.type](state, action) : state;
